import { useRef, type ClipboardEvent, type KeyboardEvent } from 'react';
import { cn } from '../../../lib/cn';
import styles from '../auth.module.css';

interface Props {
  value: string;
  onChange: (next: string) => void;
  invalid?: boolean;
  autoFocus?: boolean;
}

const LENGTH = 6;

export function OtpInput({ value, onChange, invalid, autoFocus }: Props) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  const setChar = (idx: number, char: string) => {
    const digit = char.replace(/\D/g, '').slice(0, 1);
    const chars = value.padEnd(LENGTH, ' ').split('');
    chars[idx] = digit || ' ';
    const next = chars.join('').replace(/\s+$/, '');
    onChange(next);
    if (digit && idx < LENGTH - 1) {
      refs.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (idx: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !value[idx] && idx > 0) {
      refs.current[idx - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      refs.current[idx - 1]?.focus();
      e.preventDefault();
    } else if (e.key === 'ArrowRight' && idx < LENGTH - 1) {
      refs.current[idx + 1]?.focus();
      e.preventDefault();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, LENGTH);
    if (!pasted) return;
    e.preventDefault();
    onChange(pasted);
    refs.current[Math.min(pasted.length, LENGTH - 1)]?.focus();
  };

  return (
    <div className={styles.otp} dir="ltr" role="group" aria-label="קוד אימות 6 ספרות">
      {Array.from({ length: LENGTH }).map((_, idx) => (
        <input
          key={idx}
          ref={(el) => {
            refs.current[idx] = el;
          }}
          className={cn(styles.otpInput, invalid && styles.invalid)}
          type="text"
          inputMode="numeric"
          autoComplete={idx === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={value[idx] ?? ''}
          onChange={(e) => { setChar(idx, e.target.value); }}
          onKeyDown={handleKeyDown(idx)}
          onPaste={handlePaste}
          autoFocus={autoFocus && idx === 0}
        />
      ))}
    </div>
  );
}
